import React, { useEffect, useMemo, useState } from 'react';
import { useModelGroupsQuery } from '../AdviserSettings/Page/Models/api/useModelGroupsQuery';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { HelperText } from '../CommonComponents/OPL/HelperText/HelperText';
import { Modal, ModalProps } from '../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import {
  MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES,
  ManualAccountsAssetAllocationDTO,
  ManualAccountsAssetAllocationStrategyIdDTO,
  ManualAccountsAssetAllocationTaxStatusIdDTO,
} from '../ManualAccountsAssetAllocation/types';
import { AssetAllocationDTO } from '../Profile/Accounts/api/useDefaultAssetAllocationQuery';
import { CMA } from './components/CMA/CMA';
import { MyModel } from './components/MyModel/MyModel';

interface ManualAccountsAssetAllocationModalProps extends Pick<ModalProps, 'isOpen'> {
  allocation: ManualAccountsAssetAllocationDTO;
  onChange(allocation: ManualAccountsAssetAllocationDTO): void;
  onClose(): void;
  clientId?: number;
}

export const ManualAccountsAssetAllocationModal = ({
  allocation,
  isOpen,
  onClose,
  onChange,
  clientId,
}: ManualAccountsAssetAllocationModalProps) => {
  const handleChange = (newAllocation: ManualAccountsAssetAllocationDTO) => {
    onChange({
      ...newAllocation,
      details:
        newAllocation.details &&
        newAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.CMA
          ? Object.entries(newAllocation.details).reduce((acc, [taxStatusId, allocations]) => {
              if (Array.isArray(allocations)) {
                return {
                  ...acc,
                  [taxStatusId]: allocations.map(a => ({
                    ...a,
                    y: a.y / 100,
                  })),
                };
              }

              return acc;
            }, {} as Record<ManualAccountsAssetAllocationTaxStatusIdDTO, AssetAllocationDTO[]>)
          : newAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model
          ? newAllocation.details
          : null,
    });
  };

  const [currentAllocation, setCurrentAllocation] = useState<ManualAccountsAssetAllocationDTO>({
    ...allocation,
    details:
      allocation.details && allocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.CMA
        ? Object.entries(allocation.details).reduce((acc, [taxStatusId, allocations]) => {
            if (Array.isArray(allocations)) {
              return {
                ...acc,
                [taxStatusId]: allocations.map(a => ({
                  ...a,
                  y: a.y * 100,
                })),
              };
            }

            return acc;
          }, {} as Record<ManualAccountsAssetAllocationTaxStatusIdDTO, AssetAllocationDTO[]>)
        : allocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model
        ? allocation.details
        : null,
  });

  const [showValidationError, setShowValidationError] = useState(false);

  const { data: modelGroups } = useModelGroupsQuery(clientId ? { clientId } : undefined);

  useEffect(() => {
    if (currentAllocation.details === null) {
      if (currentAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.CMA) {
        if (modelGroups) {
          setCurrentAllocation({
            ...allocation,
            details: MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES.reduce(
              (acc, taxStatusId) => ({
                ...acc,
                [taxStatusId]: modelGroups.options.defaultAssetAllocation.map(a => ({
                  ...a,
                  y: a.y * 100,
                })),
              }),
              {} as Record<ManualAccountsAssetAllocationTaxStatusIdDTO, AssetAllocationDTO[]>,
            ),
          });
        }
      }
    }
  }, [currentAllocation.strategy]);

  const isAllocationValid = useMemo(() => {
    if (
      currentAllocation.strategy !== ManualAccountsAssetAllocationStrategyIdDTO.CMA &&
      currentAllocation.strategy !== ManualAccountsAssetAllocationStrategyIdDTO.Model
    ) {
      return true;
    }

    if (!currentAllocation.details) {
      return false;
    }

    if (currentAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model) {
      return MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES.every(taxStatusId => {
        return !!currentAllocation.details![taxStatusId];
      });
    }

    return MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES.every(taxStatusId => {
      const allocations = currentAllocation.details![taxStatusId] as AssetAllocationDTO[];
      if (!allocations) {
        return false;
      }

      const total = Math.round(
        allocations.reduce((sum, allocation) => sum + (allocation.y || 0), 0),
      );

      return total === 100;
    });
  }, [currentAllocation]);

  useEffect(() => {
    if (showValidationError && isAllocationValid) {
      setShowValidationError(false);
    }
  }, [currentAllocation, isAllocationValid, showValidationError]);

  if (
    ![
      ManualAccountsAssetAllocationStrategyIdDTO.CMA,
      ManualAccountsAssetAllocationStrategyIdDTO.Model,
    ].includes(currentAllocation.strategy)
  ) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} aria={{ labelledby: 'modal-header' }} size="xl">
      <ModalHeader id="modal-header">Manage Default Asset Allocation by Tax Status</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {currentAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.CMA && (
          <CMA
            currentAllocation={currentAllocation}
            setCurrentAllocation={setCurrentAllocation}
            clientId={clientId}
          />
        )}

        {currentAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model && (
          <MyModel
            currentAllocation={currentAllocation}
            setCurrentAllocation={setCurrentAllocation}
            clientId={clientId}
          />
        )}
      </ModalBody>

      <ModalFooter>
        {showValidationError && !isAllocationValid && (
          <Box flex="1" mr="8">
            <HelperText variant="error">
              {currentAllocation.strategy === ManualAccountsAssetAllocationStrategyIdDTO.Model
                ? 'Please select a model for all tax statuses'
                : 'Please ensure all tax statuses have allocations that total 100%'}
            </HelperText>
          </Box>
        )}

        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button
          ml="8"
          onClick={() => {
            if (!isAllocationValid) {
              setShowValidationError(true);
              return;
            }

            handleChange(currentAllocation);
            onClose();
          }}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
